import { DIARY_CARD_SKILLS, DIARY_CARD_UW_EMOTIONS } from '@cc/schema';

/**
 * E37 · Diary card on the portal — the client hub's Diary card tab (DiaryCardWeek review, DefineDiaryCard for THIS
 * practice's card, FillDiaryCardInSession) and the backoffice's template catalogue (PublishDiaryCardTemplate). The field
 * lists the dynamic GraphQL client sends, the types of what comes back, and the pure bits the screens share. No React.
 */
export const TARGET_FIELDS = [
  'targetId',
  'name',
  'kind',
  'max',
  'countable',
  'standard',
  'faTargetId',
];

export const RATING_FIELDS = ['targetId', 'urge', 'scale', 'acted', 'count', 'text'];

export const WEEK_FIELDS = [
  'subject',
  'weekOf',
  {
    cards: [
      'cardId',
      'practiceId',
      'setUpBy',
      'setUpByName',
      'version',
      { targets: TARGET_FIELDS },
      { emotions: ['id'] },
      { skills: ['skillId', 'module'] },
      {
        dayEntries: [
          'cardId',
          'day',
          'enteredInSession',
          { ratings: RATING_FIELDS },
          { emotionRatings: ['id', 'rating'] },
          'skillsUsed',
          'skillsPracticed',
          'note',
        ],
      },
      'inSessionDays',
      { highlights: ['day', 'targetId', 'urge', 'acted', 'count'] },
    ],
  },
];

export const TEMPLATE_FIELDS = [
  'templateId',
  'name',
  'summary',
  { targets: TARGET_FIELDS },
  { emotions: ['id'] },
  { skills: ['skillId', 'module'] },
];

export const DEFINE_FIELDS = ['cardId', 'version', 'recorded'];
export const FILL_FIELDS = ['cards', 'recorded'];

export type Target = {
  targetId: string;
  name: string;
  kind: 'urge' | 'scale' | 'action' | 'count' | 'text';
  max?: number | null;
  countable?: boolean | null;
  standard?: boolean | null;
  faTargetId?: string | null;
};

export type Rating = {
  targetId: string;
  urge?: number | null;
  scale?: number | null;
  acted?: boolean | null;
  count?: number | null;
  text?: string | null;
};

export type DayEntry = {
  cardId: string;
  day: string;
  enteredInSession: boolean;
  ratings: Rating[];
  emotionRatings: Array<{ id: string; rating: number }>;
  skillsUsed?: number | null;
  skillsPracticed: string[];
  note?: string | null;
};

export type Highlight = {
  day: string;
  targetId: string;
  urge?: number | null;
  acted?: boolean | null;
  count?: number | null;
};

export type WeekGrid = {
  cardId: string;
  practiceId?: string | null;
  setUpBy: 'practice' | 'client';
  setUpByName?: string | null;
  version: number;
  targets: Target[];
  emotions: Array<{ id: string }>;
  skills: Array<{ skillId: string; module: string }>;
  dayEntries: DayEntry[];
  inSessionDays: string[];
  highlights: Highlight[];
};

export type WeekRow = { subject: string; weekOf: string; cards: WeekGrid[] };

export type TemplateRow = {
  templateId: string;
  name: string;
  summary: string;
  targets: Target[];
  emotions: Array<{ id: string }>;
  skills: Array<{ skillId: string; module: string }>;
};

export const KIND_CHOICES = ['urge', 'scale', 'action', 'count', 'text'] as const;

/** A name from the catalogue in the viewer's language (`diaryCard.row.<id>`), else the words the card's author wrote. */
export const rowLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  targetId: string,
  stored: string,
): string => {
  const key = `diaryCard.row.${targetId}`;
  const v = t(key);
  return v && v !== key ? v : stored;
};

export const emotionLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  id: string,
  names: (id: number) => string,
): string => {
  if (/^\d+$/.test(id)) return names(Number(id));
  const key = `diaryCard.emotion.${id}`;
  const v = t(key);
  return v && v !== key ? v : id;
};

export const dayOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

/** Today, then up to 7 days back — the days an in-session fill can be for. */
export const fillableDays = (now: Date): string[] =>
  Array.from({ length: 8 }, (_, back) =>
    dayOf(new Date(now.getFullYear(), now.getMonth(), now.getDate() - back)),
  );

/** The seven days from the monday `weekOf`. */
export const weekDays = (weekOf: string): string[] => {
  const [y, m, d] = weekOf.split('-').map(Number);
  return Array.from({ length: 7 }, (_, i) => dayOf(new Date(y, m - 1, d + i)));
};

export const shiftWeek = (weekOf: string, delta: number): string => {
  const [y, m, d] = weekOf.split('-').map(Number);
  return dayOf(new Date(y, m - 1, d + delta * 7));
};

/** One grid cell: the number, ✓ / – for yes / no, or the text. */
export const cellOf = (r: Rating | undefined): string => {
  if (!r) return '';
  if (r.urge != null) return String(r.urge);
  if (r.scale != null) return String(r.scale);
  if (r.count != null) return String(r.count);
  if (r.acted != null) return r.acted ? '✓' : '–';
  return r.text ?? '';
};

export const isHighlighted = (
  highlights: readonly Highlight[],
  day: string,
  targetId: string,
): boolean => highlights.some((h) => h.day === day && h.targetId === targetId);

/** A UUIDv7 minted in the browser — the fill's id, so a retried Save is the same fact. */
export const newId = (now: number = Date.now()): string => {
  const hex = (count: number) =>
    Array.from({ length: count }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join('');
  const time = now.toString(16).padStart(12, '0');
  const variant = ['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)];
  return `${time.slice(0, 8)}-${time.slice(8, 12)}-7${hex(3)}-${variant}${hex(
    3,
  )}-${hex(12)}`;
};

// ── the card editor ──────────────────────────────────────────────────────────────────────────────

export type CardDraft = {
  cardId: string | null;
  template: string;
  targets: Target[];
  emotions: string[];
  skills: string[];
};

export const blankDraft = (): CardDraft => ({
  cardId: null,
  template: 'custom',
  targets: [],
  emotions: [],
  skills: [],
});

const cleanTarget = (t: Target): Target => ({
  targetId: t.targetId,
  name: t.name,
  kind: t.kind,
  ...(t.max != null ? { max: t.max } : {}),
  ...(t.countable ? { countable: true } : {}),
  ...(t.standard ? { standard: true } : {}),
  ...(t.faTargetId ? { faTargetId: t.faTargetId } : {}),
});

export const draftFromTemplate = (tpl: TemplateRow): CardDraft => ({
  cardId: null,
  template: tpl.templateId,
  targets: tpl.targets.map(cleanTarget),
  emotions: tpl.emotions.map((e) => e.id),
  skills: tpl.skills.map((s) => s.skillId),
});

export const draftFromCard = (card: WeekGrid, template = 'custom'): CardDraft => ({
  cardId: card.cardId,
  template,
  targets: card.targets.map(cleanTarget),
  emotions: card.emotions.map((e) => e.id),
  skills: card.skills.map((s) => s.skillId),
});

export const newTargetId = (): string => `t-${newId().replace(/-/g, '').slice(-8)}`;

export const patchTarget = (
  draft: CardDraft,
  targetId: string,
  patch: Partial<Target>,
): CardDraft => ({
  ...draft,
  targets: draft.targets.map((t) => {
    if (t.targetId !== targetId) return t;
    const next = { ...t, ...patch };
    if (next.kind !== 'urge' && next.kind !== 'scale') delete next.max;
    else if (next.max == null) next.max = 5;
    if (next.kind !== 'action') delete next.countable;
    // a catalogue row renamed is the author's own row now
    if (patch.name !== undefined && t.standard) next.standard = false;
    return next;
  }),
});

export const draftProblem = (d: CardDraft): 'empty' | 'unnamed' | null => {
  if (!d.targets.length && !d.emotions.length && !d.skills.length) return 'empty';
  return d.targets.some((t) => !t.name.trim()) ? 'unnamed' : null;
};

const moduleOf = (skillId: string): string =>
  DIARY_CARD_SKILLS.find((s) => s.skillId === skillId)?.module ?? 'mindfulness';

export const defineInputOf = (subject: string | null, d: CardDraft) => ({
  ...(subject ? { subject } : {}),
  ...(d.cardId ? { cardId: d.cardId } : {}),
  template: d.template,
  targets: d.targets.map((t) => ({ ...cleanTarget(t), name: t.name.trim() })),
  emotions: d.emotions.map((id) => ({ id })),
  skills: d.skills.map((skillId) => ({ skillId, module: moduleOf(skillId) })),
});

export const publishInputOf = (
  templateId: string,
  name: string,
  summary: string,
  d: CardDraft,
) => ({
  templateId: templateId.trim(),
  name: name.trim(),
  summary: summary.trim(),
  targets: d.targets.map((t) => ({ ...cleanTarget(t), name: t.name.trim() })),
  emotions: d.emotions.map((id) => ({ id })),
  skills: d.skills.map((skillId) => ({ skillId, module: moduleOf(skillId) })),
});

export const UW_EMOTIONS: readonly string[] = DIARY_CARD_UW_EMOTIONS;
export const SKILLS = DIARY_CARD_SKILLS;

// ── the in-session fill ──────────────────────────────────────────────────────────────────────────

export type RowValue = number | boolean | string | null;

export type FillDraft = {
  day: string;
  values: Record<string, RowValue>;
  emotions: Record<string, number>;
  skillsUsed: number | null;
  skillsPracticed: string[];
  note: string;
};

export const emptyFill = (day: string): FillDraft => ({
  day,
  values: {},
  emotions: {},
  skillsUsed: null,
  skillsPracticed: [],
  note: '',
});

const ratingOf = (t: Target, value: RowValue): Record<string, unknown> | null => {
  if (value === null || value === '') return null;
  const base = { targetId: t.targetId };
  switch (t.kind) {
    case 'urge':
      return typeof value === 'number' ? { ...base, urge: value } : null;
    case 'scale':
      return typeof value === 'number' ? { ...base, scale: value } : null;
    case 'action':
      return t.countable
        ? typeof value === 'number'
          ? { ...base, count: value }
          : null
        : typeof value === 'boolean'
        ? { ...base, acted: value }
        : null;
    case 'count':
      return typeof value === 'number' ? { ...base, count: value } : null;
    case 'text':
      return typeof value === 'string' && value.trim()
        ? { ...base, text: value.trim() }
        : null;
    default:
      return null;
  }
};

/** The therapist's fill as the mutation's input — only what was given; null when nothing was. */
export const fillInputOf = (
  subject: string,
  fillId: string,
  card: Pick<WeekGrid, 'targets'>,
  d: FillDraft,
) => {
  const ratings = card.targets
    .map((t) => ratingOf(t, d.values[t.targetId] ?? null))
    .filter((r): r is Record<string, unknown> => r !== null);
  const emotionRatings = Object.entries(d.emotions).map(([id, rating]) => ({
    id,
    rating,
  }));
  const note = d.note.trim();
  if (
    !ratings.length &&
    !emotionRatings.length &&
    d.skillsUsed === null &&
    !d.skillsPracticed.length &&
    !note
  ) {
    return null;
  }
  return {
    subject,
    fillId,
    day: d.day,
    ...(ratings.length ? { ratings } : {}),
    ...(emotionRatings.length ? { emotionRatings } : {}),
    ...(d.skillsUsed !== null ? { skillsUsed: d.skillsUsed } : {}),
    ...(d.skillsPracticed.length ? { skillsPracticed: d.skillsPracticed } : {}),
    ...(note ? { note } : {}),
  };
};

export const scaleValues = (max: number | null | undefined): number[] =>
  Array.from({ length: (max ?? 5) + 1 }, (_, i) => i);
